import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'zh-CN', name: '中文', flag: '🇨🇳' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' }
];

// CSS rules for hiding Google Translate UI - static, no XSS risk
const GT_HIDE_STYLES = `
  .goog-te-banner-frame { display: none !important; }
  body { top: 0 !important; }
  .skiptranslate { display: none !important; }
  iframe.skiptranslate { display: none !important; }
  #google_translate_element .goog-te-gadget { font-size: 0 !important; }
  #google_translate_element { position: absolute; top: -9999px; left: -9999px; opacity: 0; pointer-events: none; }
`;

const LanguageTranslator = () => {
  const [currentLanguage, setCurrentLanguage] = useState(LANGUAGES[0]);
  const [ready, setReady] = useState(false);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    // Inject styles using textContent (safe, no XSS risk)
    if (!document.getElementById('gt-hide-style')) {
      const style = document.createElement('style');
      style.id = 'gt-hide-style';
      style.textContent = GT_HIDE_STYLES;
      document.head.appendChild(style);
    }

    // Load Google Translate script
    if (!document.getElementById('google-translate-script')) {
      window.googleTranslateElementInit = function () {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'es,fr,zh-CN,ar,vi,ko,hi',
            autoDisplay: false,
          },
          'google_translate_element'
        );
        // Poll for readiness
        pollIntervalRef.current = setInterval(() => {
          const sel = document.querySelector('.goog-te-combo');
          if (sel) {
            clearInterval(pollIntervalRef.current);
            setReady(true);
          }
        }, 300);
      };

      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      document.body.appendChild(script);
    } else {
      // Already loaded, check if combo exists
      const sel = document.querySelector('.goog-te-combo');
      if (sel) setReady(true);
    }

    // Detect current language from cookie
    const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
    if (match) {
      const lang = LANGUAGES.find((l) => l.code === match[1]);
      if (lang) setCurrentLanguage(lang);
    }

    // Cleanup interval on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const triggerTranslate = useCallback((langCode) => {
    const combo = document.querySelector('.goog-te-combo');
    if (!combo) return;

    combo.value = langCode;
    combo.dispatchEvent(new Event('change', { bubbles: true }));
  }, []);

  const translatePage = useCallback((langCode) => {
    const selected = LANGUAGES.find((l) => l.code === langCode);
    if (!selected) return;
    setCurrentLanguage(selected);

    if (langCode === 'en') {
      // Reset to English: clear cookies then reload
      const domain = window.location.hostname;
      const cookieName = 'googtrans';
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
      window.location.reload();
    } else {
      triggerTranslate(langCode);
    }
  }, [triggerTranslate]);

  return (
    <div className="relative z-50">
      <div id="google_translate_element"></div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="language-selector-btn"
            variant="outline"
            size="sm"
            className="bg-white/90 border border-white/40 hover:bg-white text-darkBrown shadow-md h-8 px-2 text-xs"
          >
            <Globe size={14} className="mr-1" />
            <span className="mr-1">{currentLanguage.flag}</span>
            <span className="hidden sm:inline font-medium">{currentLanguage.name}</span>
            <ChevronDown size={12} className="ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-48 bg-white shadow-2xl border border-gray-200">
          {LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => translatePage(lang.code)}
              disabled={!ready && lang.code !== 'en'}
              className={`cursor-pointer py-2 px-3 hover:bg-primaryOrange/10 transition-colors ${
                currentLanguage.code === lang.code ? 'bg-primaryOrange/10 font-bold' : ''
              }`}
            >
              <span className="mr-2 text-lg">{lang.flag}</span>
              <span className="text-sm">{lang.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default LanguageTranslator;
